import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    detectSuggestedRegion,
    hasUserAlreadyChosenRegion,
    applyQueryParamRegion,
} from '../utils/geoRegion';

export type GeoRegionSuggestion = { code: string; name: string };

type GeoRegionContextType = {
    suggestion: GeoRegionSuggestion | null;
    clearSuggestion: () => void;
};

export const GeoRegionContext = createContext<GeoRegionContextType>({
    suggestion: null,
    clearSuggestion: () => undefined,
});

interface Props {
    preferredLanguage: string;
    children: React.ReactNode;
}

// Lives in the persistent page shell, so the suggestion survives client-side
// navigation instead of being re-detected on every page.
export const GeoRegionProvider: React.FC<Props> = ({
    preferredLanguage,
    children,
}) => {
    const [suggestion, setSuggestion] = useState<GeoRegionSuggestion | null>(
        null
    );

    useEffect(() => {
        applyQueryParamRegion();
        // A choice made anywhere (banner, country overlay, ?cc=) ends the
        // suggestion, including same-origin choices that keep this shell mounted.
        if (hasUserAlreadyChosenRegion()) {
            setSuggestion(null);
            return;
        }

        let cancelled = false;
        detectSuggestedRegion(preferredLanguage).then((result) => {
            if (cancelled || hasUserAlreadyChosenRegion()) return;
            setSuggestion(
                result?.suggestedCode && result.suggestedName
                    ? { code: result.suggestedCode, name: result.suggestedName }
                    : null
            );
        });
        return () => {
            cancelled = true;
        };
    }, [preferredLanguage]);

    const clearSuggestion = useCallback(() => setSuggestion(null), []);

    const value = useMemo(
        () => ({ suggestion, clearSuggestion }),
        [suggestion, clearSuggestion]
    );

    return (
        <GeoRegionContext.Provider value={value}>
            {children}
        </GeoRegionContext.Provider>
    );
};
